(() => {
  const MOBILE_MQ = '(max-width: 768px)';

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function parseIntAttr(el, name, fallback) {
    const v = parseInt(String(el.getAttribute(name) || ''), 10);
    return Number.isFinite(v) ? v : fallback;
  }

  function parseFloatAttr(el, name, fallback) {
    const v = parseFloat(String(el.getAttribute(name) || ''));
    return Number.isFinite(v) ? v : fallback;
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }

  function now() {
    return Date.now();
  }

  function suppressed(root) {
    if (root.getAttribute('data-ignore-suppression') === 'true') return false;
    const dismissKey = root.getAttribute('data-storage-dismiss');
    const submitKey = root.getAttribute('data-storage-submit');
    const dismissDays = parseIntAttr(root, 'data-dismiss-days', 7);
    const submitDays = parseIntAttr(root, 'data-submit-days', 180);
    const dismissAt = storageGet(dismissKey);
    const submitAt = storageGet(submitKey);
    const t = now();
    if (submitAt) {
      const ts = parseInt(submitAt, 10);
      if (Number.isFinite(ts) && t - ts < submitDays * 86400000) return true;
    }
    if (dismissAt) {
      const ts = parseInt(dismissAt, 10);
      if (Number.isFinite(ts) && t - ts < dismissDays * 86400000) return true;
    }
    return false;
  }

  function urlLooksSuccessful(url) {
    try {
      const u = new URL(url, window.location.origin);
      const sp = u.searchParams;
      return sp.get('customer_posted') === 'true' || sp.get('contact_posted') === 'true';
    } catch {
      return false;
    }
  }

  function getFocusable(container) {
    if (!container) return [];
    const sel =
      'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(sel)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }

  function extractResultFromHtml(html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const returnedForm =
        doc.querySelector('form#contact_form') ||
        doc.querySelector('form[action*="/contact"]') ||
        doc.querySelector('form[data-email-popup-form]');
      if (!returnedForm) return { ok: false };

      const returnedSuccess = returnedForm.querySelector('[data-email-popup-success]');
      const returnedFail =
        returnedForm.querySelector('.w-form-fail') ||
        returnedForm.querySelector('.errors') ||
        returnedForm.querySelector('[data-form-error]');

      const successShown =
        !!returnedSuccess &&
        !returnedSuccess.hasAttribute('hidden') &&
        String(returnedSuccess.style.display || '').toLowerCase() !== 'none';

      const failShown =
        !!returnedFail &&
        !returnedFail.hasAttribute('hidden') &&
        String(returnedFail.style.display || '').toLowerCase() !== 'none';

      if (successShown && !failShown) return { ok: true };
      if (failShown) return { ok: false, errorText: (returnedFail.textContent || '').trim() };
      return { ok: false };
    } catch {
      return { ok: false };
    }
  }

  function injectCustomEmbed(root) {
    const payload = root.querySelector('[data-email-popup-custom-payload]');
    const mount = root.querySelector('[data-email-popup-custom]');
    if (!payload || !mount) return;
    try {
      const html = JSON.parse(payload.textContent || '""');
      if (typeof html === 'string' && html) mount.innerHTML = html;
    } catch {
      /* ignore */
    }
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
    }
  }

  function init(root) {
    if (!root || suppressed(root)) return;

    injectCustomEmbed(root);

    const submitKey = root.getAttribute('data-storage-submit');
    const dismissKey = root.getAttribute('data-storage-dismiss');
    const integration = (root.getAttribute('data-integration') || 'shopify').toLowerCase();
    const promoUrl = (root.getAttribute('data-promo-url') || '').trim();
    const closeOverlay = root.getAttribute('data-close-overlay') !== 'false';
    const forcePreviewOpen = root.getAttribute('data-force-preview-open') === 'true';

    if (urlLooksSuccessful(window.location.href)) {
      storageSet(submitKey, String(now()));
      return;
    }

    const serverSuccess = root.querySelector('[data-email-popup-success]');
    if (serverSuccess && !serverSuccess.hasAttribute('hidden')) {
      storageSet(submitKey, String(now()));
      return;
    }

    const backdrop = root.querySelector('[data-email-popup-backdrop]');
    const closeBtns = root.querySelectorAll('[data-email-popup-close]');
    const form = root.querySelector('form[data-email-popup-form]');
    const formFields = root.querySelector('[data-email-popup-form-fields]');
    const errEl = root.querySelector('[data-email-popup-error]');
    const successEl = root.querySelector('[data-email-popup-success]');
    const discountWrap = root.querySelector('[data-email-popup-discount-wrap]');
    const mobile = window.matchMedia(MOBILE_MQ).matches;
    const desktopTrigger = root.getAttribute('data-desktop-trigger') || 'exit_intent';
    const mobileTrigger = root.getAttribute('data-mobile-trigger') || 'delay';
    const delaySec = parseIntAttr(root, 'data-delay-seconds', 12);
    const scrollPct = parseFloatAttr(root, 'data-scroll-percent', 40);

    let opened = false;
    let lastFocus = null;
    let fallbackTimer = null;
    let exitIntentHandler = null;

    function markSubmitted() {
      storageSet(submitKey, String(now()));
    }

    function markDismissed() {
      storageSet(dismissKey, String(now()));
    }

    function open() {
      if (opened) return;
      opened = true;
      if (fallbackTimer != null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      if (exitIntentHandler) {
        document.documentElement.removeEventListener('mouseout', exitIntentHandler);
        exitIntentHandler = null;
      }
      lastFocus = document.activeElement;
      setHidden(root, false);
      document.documentElement.classList.add('email-popup--open');

      const focusTarget =
        root.querySelector('input[type="email"]') || root.querySelector('button, a[href], input');
      if (focusTarget && typeof focusTarget.focus === 'function') {
        setTimeout(() => focusTarget.focus(), 10);
      }
    }

    function close() {
      if (forcePreviewOpen) return;
      if (root.hasAttribute('hidden')) return;
      setHidden(root, true);
      document.documentElement.classList.remove('email-popup--open');
      markDismissed();
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try {
          lastFocus.focus();
        } catch {
          /* ignore */
        }
      }
    }

    function scheduleOpen(cb) {
      if (suppressed(root)) return undefined;
      return () => {
        if (!opened && !suppressed(root)) cb();
      };
    }

    function attachTriggers() {
      const tryOpen = scheduleOpen(open);
      if (!tryOpen) return;

      if (mobile) {
        if (mobileTrigger === 'manual') {
          return;
        }
        if (mobileTrigger === 'delay') {
          setTimeout(tryOpen, delaySec * 1000);
        } else if (mobileTrigger === 'scroll') {
          let done = false;
          const onScroll = () => {
            if (done || opened) return;
            const doc = document.documentElement;
            const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
            const pct = (window.scrollY / scrollable) * 100;
            if (pct >= scrollPct) {
              done = true;
              window.removeEventListener('scroll', onScroll, { passive: true });
              tryOpen();
            }
          };
          window.addEventListener('scroll', onScroll, { passive: true });
          onScroll();
        }
      } else {
        if (desktopTrigger === 'manual') {
          return;
        }
        if (desktopTrigger === 'exit_intent') {
          let exitFired = false;
          exitIntentHandler = (e) => {
            if (exitFired || opened) return;
            if (e.relatedTarget != null) return;
            if (e.clientY > 40) return;
            exitFired = true;
            document.documentElement.removeEventListener('mouseout', exitIntentHandler);
            exitIntentHandler = null;
            if (fallbackTimer != null) {
              clearTimeout(fallbackTimer);
              fallbackTimer = null;
            }
            tryOpen();
          };
          document.documentElement.addEventListener('mouseout', exitIntentHandler);
          fallbackTimer = setTimeout(() => {
            fallbackTimer = null;
            if (exitIntentHandler) {
              document.documentElement.removeEventListener('mouseout', exitIntentHandler);
              exitIntentHandler = null;
            }
            if (!opened) tryOpen();
          }, delaySec * 1000);
        } else if (desktopTrigger === 'delay') {
          setTimeout(tryOpen, delaySec * 1000);
        } else if (desktopTrigger === 'scroll') {
          let done = false;
          const onScroll = () => {
            if (done || opened) return;
            const doc = document.documentElement;
            const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
            const pct = (window.scrollY / scrollable) * 100;
            if (pct >= scrollPct) {
              done = true;
              window.removeEventListener('scroll', onScroll, { passive: true });
              tryOpen();
            }
          };
          window.addEventListener('scroll', onScroll, { passive: true });
          onScroll();
        }
      }
    }

    document.querySelectorAll('[data-email-popup-open]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        if (!suppressed(root)) open();
      });
    });

    closeBtns.forEach((btn) => btn.addEventListener('click', () => close()));
    if (closeOverlay && backdrop) {
      backdrop.addEventListener('click', () => close());
    }

    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    });

    root.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || root.hasAttribute('hidden')) return;
      const focusables = getFocusable(root);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    function showFormSuccess() {
      if (formFields) setHidden(formFields, true);
      if (errEl) {
        errEl.textContent = '';
        setHidden(errEl, true);
      }
      if (successEl) setHidden(successEl, false);
      if (discountWrap) setHidden(discountWrap, false);
      markSubmitted();
      if (promoUrl) {
        setTimeout(() => {
          window.location.href = promoUrl;
        }, 2800);
      }
    }

    function showFormError(msg) {
      if (!errEl) return;
      errEl.textContent = msg || 'Something went wrong. Please try again.';
      setHidden(errEl, false);
    }

    if (form && integration === 'shopify') {
      const submitBtn = form.querySelector('[data-email-popup-submit]');
      const emailInput = form.querySelector('input[type="email"]');

      function syncFormHasEmailState() {
        if (!emailInput) return;
        const hasEmail = String(emailInput.value || '').trim().length > 0;
        form.classList.toggle('has-email', hasEmail);
      }

      if (emailInput) {
        emailInput.addEventListener('input', syncFormHasEmailState);
        emailInput.addEventListener('change', syncFormHasEmailState);
        syncFormHasEmailState();
      }

      let actionUrl = null;
      try {
        actionUrl = new URL(form.getAttribute('action') || window.location.href, window.location.origin);
      } catch {
        actionUrl = null;
      }
      const host = String(window.location.hostname || '').toLowerCase();
      const isLocalhost = host === 'localhost' || host === '127.0.0.1';
      const isSameOrigin = actionUrl ? actionUrl.origin === window.location.origin : true;

      if (!isLocalhost && isSameOrigin) {
        form.addEventListener(
          'submit',
          async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

            if (errEl) {
              errEl.textContent = '';
              setHidden(errEl, true);
            }
            if (submitBtn) submitBtn.disabled = true;

            try {
              const fd = new FormData(form);
              const body = new URLSearchParams();
              for (const [k, v] of fd.entries()) {
                body.append(k, typeof v === 'string' ? v : '');
              }
              const action = actionUrl ? actionUrl.toString() : form.getAttribute('action') || window.location.href;

              const res = await fetch(action, {
                method: 'POST',
                body,
                credentials: 'same-origin',
                redirect: 'follow',
                headers: {
                  Accept: 'text/html',
                  'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                },
              });

              if (!res.ok) throw new Error(`HTTP ${res.status}`);

              const html = await res.text();
              const parsed = extractResultFromHtml(html);
              const ok =
                parsed.ok || urlLooksSuccessful(res.url) || (res.redirected && !parsed.errorText);

              if (ok) {
                if (emailInput) emailInput.value = '';
                syncFormHasEmailState();
                showFormSuccess();
              } else {
                const errorText = (parsed.errorText || '').replace(/\s+/g, ' ').trim();
                showFormError(errorText);
              }
            } catch {
              try {
                form.submit();
              } catch {
                showFormError();
              }
            } finally {
              if (submitBtn && formFields && !formFields.hasAttribute('hidden')) {
                submitBtn.disabled = false;
              }
            }
          },
          true,
        );
      }
    }

    if (forcePreviewOpen) {
      open();
    } else {
      attachTriggers();
    }
  }

  onReady(() => {
    document.querySelectorAll('[data-email-popup-root]').forEach(init);
  });
})();
